import { collectOffsetPages } from '../../shared/collectOffsetPages';
import type { ApiClient } from '../../api/client';
import type {
  CertificateDocument,
  CertificateTemplate,
  CertificateVerification,
  CertificateVerificationPort,
  CertificatesPort,
} from '../../features/certificates/public';

type CertificateCollection<T> = { total: number; data: T[] };
type CertificatesResponse<K extends keyof CertificatesPort> = Awaited<ReturnType<CertificatesPort[K]>>;
type CollectionItem<T> = T extends Array<infer Item> ? Item : never;

const pageSize = 100;

async function listAll<T extends { id: string }>(
  client: ApiClient,
  pathForOffset: (offset: number) => string,
): Promise<T[]> {
  return collectOffsetPages((offset) => client.requestTenant<CertificateCollection<T>>(pathForOffset(offset)), pageSize).then((response) => response.data);
}

export function createCertificatesHttpAdapter(client: ApiClient): CertificatesPort {
  return {
    listAvailableCertificateTypes() {
      return client.requestTenant<CertificateCollection<CertificateTemplate>>(
        '/api/hr/v1/{tenant}/certificateTypes/available',
      ).then((response) => response.data);
    },
    previewCertificate(type) {
      return client.requestTenant<CertificatesResponse<'previewCertificate'>>(
        `/api/hr/v1/{tenant}/certificatePreview?type=${encodeURIComponent(type)}`,
      );
    },
    issueCertificate(input, idempotencyKey = crypto.randomUUID()) {
      return client.requestTenant<CertificatesResponse<'issueCertificate'>>(
        '/api/hr/v1/{tenant}/certificateRequests',
        {
          method: 'POST',
          headers: { 'Idempotency-Key': idempotencyKey },
          body: JSON.stringify(input),
        },
      );
    },
    listMyCertificateRequests() {
      return listAll<CollectionItem<CertificatesResponse<'listMyCertificateRequests'>>>(
        client,
        (offset) => `/api/hr/v1/{tenant}/certificateRequests/mine?limit=${pageSize}&offset=${offset}`,
      );
    },
    listDocuments() {
      return client.listDocuments().then((response) => response.data satisfies CertificateDocument[]);
    },
    downloadDocument(documentId) {
      return client.downloadDocument(documentId);
    },
    listCertificateOperations() {
      return listAll<CollectionItem<CertificatesResponse<'listCertificateOperations'>>>(
        client,
        (offset) => `/api/hr/v1/{tenant}/certificateRequests?limit=${pageSize}&offset=${offset}`,
      );
    },
    revokeCertificate(issuanceId, reason) {
      return client.requestTenant<CertificatesResponse<'revokeCertificate'>>(
        `/api/hr/v1/{tenant}/certificateIssuances/${encodeURIComponent(issuanceId)}/revoke`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      );
    },
    listCertificateSettings() {
      return client.requestTenant<CertificateCollection<CertificateTemplate>>(
        '/api/hr/v1/{tenant}/certificateSettings',
      ).then((response) => response.data);
    },
    updateCertificateSetting(type, input) {
      const {
        version,
        displayName,
        statement,
        requireSeal,
        autoIssue,
        validityDays,
        verificationPublic,
        enabled,
      } = input;
      return client.requestTenant<CertificatesResponse<'updateCertificateSetting'>>(
        `/api/hr/v1/{tenant}/certificateSettings/${encodeURIComponent(type)}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            version,
            displayName,
            statement,
            requireSeal,
            autoIssue,
            validityDays,
            verificationPublic,
            enabled,
          }),
        },
      );
    },
  };
}

export function createCertificateVerificationHttpAdapter(
  fetchImplementation: typeof fetch = fetch,
): CertificateVerificationPort {
  return {
    async verifyCertificate(token) {
      const response = await fetchImplementation(
        `/api/public/certificates/${encodeURIComponent(token)}`,
        { headers: { Accept: 'application/json' } },
      );
      if (!response.ok) throw new Error('증명서 검증 정보를 불러오지 못했습니다.');
      return response.json() as Promise<CertificateVerification>;
    },
  };
}
